/**
 * The waste rules the Sync dialog seeds with and the Waste card judges by —
 * one copy, because the two must agree exactly: the card tags a recorded
 * figure "estimated" only when it equals the seed the dialog offered, and
 * "capped" only when the leftover fell under the same tenth. A second copy
 * would not fail to compile; the tags would just quietly go wrong the day
 * someone changed the rate on one side.
 */

/** Up to a tenth of the batch is assumed lost by the time it is synced. */
export const WASTE_RATE = 0.1;

/** The horizons the projection states, in days. The chart places its dashed
 *  marks at exactly these offsets, so they have to be the same numbers. */
export const WASTE_HALF_YEAR_DAYS = 182;
export const WASTE_YEAR_DAYS = 365;

const round1 = (n: number): number => Math.round(n * 10) / 10;

/**
 * The cap the seed is held under: a tenth of the batch, at the one-decimal
 * precision every figure in this feature is kept at. Rounded BEFORE any
 * comparison — `3 * 0.1` is `0.30000000000000004` in floating point, and an
 * unrounded threshold tagged a leftover of exactly 0.3 t as "under a tenth".
 */
export function wasteCap(startingStock: number): number {
  return round1(startingStock * WASTE_RATE);
}

/**
 * The Waste field's seed: a tenth of what the cycle OPENED with, capped at
 * what is still on site — a loss cannot exceed the leftover it comes out of.
 * Null when there is nothing to estimate from (no open batch, or the leftover
 * is not a usable number yet), so the field can stay blank rather than assert
 * a figure from nothing. One decimal, like `estimatedRemaining`.
 */
export function estimateWaste(
  startingStock: number | null | undefined,
  remaining: number,
): number | null {
  if (startingStock == null || !Number.isFinite(remaining) || remaining < 0) return null;
  return Math.min(wasteCap(startingStock), round1(remaining));
}

/** Less than a tenth of the batch was left, so the seed was the leftover. */
export function isWasteCapped(startingStock: number, remaining: number): boolean {
  return round1(remaining) < wasteCap(startingStock);
}
